import {
  GridColDef,
  GridFilterModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { UserKeys, getIndexedPageGroups } from '../../../../api/users-api';
import { useState, memo, useMemo } from 'react';
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { AddUserGroup, Group, BigQueryPage } from '../../../../../types';
import StepGroupsToolbar from './step-groups-toolbar';
import { UserFormValues } from '../user-schema';
import SharedDataGridServer from '../../../../shared/shared-data-grid-server';

import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { FormSectionHeader } from '../../../../shared/form-section-header';

// Memoized role selector component to prevent unnecessary re-renders
const RoleSelector = memo(
  ({
    groupId,
    groupIndex,
    control,
  }: {
    groupId: string;
    groupIndex: number;
    control: Control<UserFormValues>;
  }) => {
    return (
      <Controller
        key={groupId}
        name={`groups.${groupIndex}.member.role`}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            slotProps={{
              input: {
                id: `group-role-${groupId}`,
              },
              inputLabel: {
                htmlFor: `group-role-${groupId}`,
              },
            }}
            label="Role"
            select
            size="small"
            margin="none"
            defaultValue="MEMBER"
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="MEMBER">Member</MenuItem>
            <MenuItem value="MANAGER">Manager</MenuItem>
            <MenuItem value="OWNER">Owner</MenuItem>
          </TextField>
        )}
      />
    );
  }
);

interface StepGroupsProps {
  setValue: UseFormSetValue<UserFormValues>;
  watch: UseFormWatch<UserFormValues>;
  getValues: UseFormGetValues<UserFormValues>;
  control: Control<UserFormValues>;
}

export default function StepGroupsIndexed({
  setValue,
  watch,
  control,
  getValues,
}: StepGroupsProps) {
  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
    search: string;
  }>({
    pageSize: 100,
    page: 0,
    search: '',
  });

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      UserKeys.GroupsIndexedPage,
      paginationModel.page,
      paginationModel.pageSize,
      filterModel,
    ],
    queryFn: () => {
      const object: BigQueryPage<Group[]> = {
        results: [],
        pageSize: paginationModel.pageSize,
        currentPage: paginationModel.page,
        totalCount: 0,
      };

      const sortModel: GridSortModel = [{ field: 'email', sort: 'asc' }];
      return getIndexedPageGroups(object, filterModel, sortModel);
    },
    placeholderData: keepPreviousData,
    meta: {
      errorMessage:
        'There was an issue loading groups. Please refresh and try again.',
    },
  });

  const groups = watch('groups');
  const currentGroups = useMemo(() => groups || [], [groups]);
  const [selectedRows, setSelectedRows] = useState<string[]>(
    currentGroups.map((g) => g.groupId) ?? []
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'groupEmail',
        headerName: 'Groups',
        sortable: false,
        filterable: false,
        display: 'flex',
        flex: 1,
        renderCell: (params) => {
          const groupIndex = currentGroups.findIndex(
            (g) => g.groupId === params.value
          );
          const isSelected = groupIndex !== -1;

          return (
            <Stack
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              sx={{ mr: 4 }}
            >
              <Typography variant="body2">{params.value}</Typography>

              {isSelected && (
                <RoleSelector
                  groupId={params.value}
                  groupIndex={groupIndex}
                  control={control}
                />
              )}
            </Stack>
          );
        },
      },
    ],
    [control, currentGroups]
  );

  const rows = useMemo(
    () =>
      data?.results.map((group) => ({
        ...group,
        id: group.email,
        groupId: group.email,
        groupEmail: group.email,
      })) || [],
    [data?.results]
  );

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}
    >
      <FormSectionHeader description="Select any groups you'd like this user to be added to.">
        Groups
      </FormSectionHeader>
      <SharedDataGridServer
        paginationModel={paginationModel}
        onPaginationModelChange={(newModel) => {
          if (isFetching || isLoading) return;

          if (newModel.pageSize !== paginationModel.pageSize) {
            setPaginationModel({
              page: 0,
              pageSize: newModel.pageSize,
              search: paginationModel.search,
            });
          } else {
            setPaginationModel({
              ...newModel,
              search: paginationModel.search,
            });
          }
        }}
        onFilterModelChange={(model) => {
          const quickFilterValue = model.quickFilterValues?.[0] || '';

          // Remove only the filter we may have added previously
          const otherFilters = model.items.filter(
            (item) => item.field !== 'email' || item.operator !== 'like'
          );

          if (quickFilterValue) {
            otherFilters.push({
              field: 'email',
              operator: 'like',
              value: quickFilterValue,
            });
          }

          setFilterModel({
            ...model,
            items: otherFilters,
          });
        }}
        toolbarQuickFilterWidth={392}
        pageSizeOptions={[100]}
        data={data}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        onRowSelectionModelChange={(newSelection) => {
          const selectedGroupIds = Array.from(newSelection.ids) as string[];
          const newGroups: AddUserGroup[] = selectedGroupIds
            .map((groupId) => {
              const existingGroup = currentGroups.find(
                (group) => group?.groupId === groupId
              );

              if (existingGroup) return existingGroup;

              const row = rows.find((row) => row.id === groupId);

              if (!row) return null;

              return {
                groupId,
                member: {
                  email: `${getValues('userName')}@${getValues('domain')}`,
                  role: 'MEMBER',
                  canActOn: false,
                },
              };
            })
            .filter(Boolean) as AddUserGroup[];

          setValue('groups', newGroups);
        }}
        slots={{
          toolbar: StepGroupsToolbar,
        }}
        checkboxSelection
        disableColumnMenu
        keepNonExistentRowsSelected
        columns={columns}
        rows={rows || []}
        loading={isLoading || isFetching}
        sx={{
          mt: 2,
          flexGrow: 1,
          minHeight: 0,
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
          minWidth: 700,
        }}
      />
    </Box>
  );
}
